import React, {PureComponent} from 'react';
import PropTypes from 'prop-types';
import reduce from 'lodash/reduce';
import find from 'lodash/find';
import isEmpty from 'lodash/isEmpty';
import {gettext} from '../translations';

import CalendarSVG from 'eventbrite_design_system/iconography/icons/Calendar';
import UserSVG from 'eventbrite_design_system/iconography/icons/User';
import HeartSVG from 'eventbrite_design_system/iconography/icons/Heart';

import ConnectedEventsBucket from '../containers/ConnectedEventsBucket';
import ConnectedOrganizersBucket from '../containers/ConnectedOrganizersBucket';
import ConnectedOrganizerArticleBucket from '../containers/ConnectedOrganizerArticleBucket';
import {getBuckets} from '../utils/feed';
import {
    PROFILE_TYPE,
    ORGANIZER_ARTICLE_TYPE,
} from '../constants/constants';
import {
    BUCKETS_PROPTYPES,
    FEED_CONFIG_PROPTYPES,
    FEATURE_FLAGS_PROPTYPES,
    EVENT_SEARCH_PROPTYPES,
    FOLLOW_PROPTYPES,
} from '../constants/propTypes';

import {
    FEED_TABS,
    FOR_YOU,
} from '../constants/tabConfig';

import LoadingBucket from './LoadingBucket';

import './EventsBucket.scss';
import StateMessage from './StateMessage';

export default class FeedBuilderTabs extends PureComponent {
    static propTypes = {
        fetchEventsForTab: PropTypes.func,
        buckets: BUCKETS_PROPTYPES.isRequired,
        events: BUCKETS_PROPTYPES.isRequired,
        featureFlags: FEATURE_FLAGS_PROPTYPES.isRequired,
        feedConfig: FEED_CONFIG_PROPTYPES.isRequired,
        eventSearch: EVENT_SEARCH_PROPTYPES,
        follow: FOLLOW_PROPTYPES,
        verifyPoorContent: PropTypes.func,
        isLoading: PropTypes.bool,
        shouldSuggestReloadingPage: PropTypes.bool,
        tabIndex: PropTypes.number,
        isAuthenticated: PropTypes.bool,
    };

    state = {
        isInBrowser: false,
    }

    componentDidMount() {
        const {
            buckets,
            events,
            verifyPoorContent,
        } = this.props;

        if (verifyPoorContent) {
            verifyPoorContent({buckets, events, actionName: 'on_tab_loading'});
        }

        this.setState({isInBrowser: true});
    }

    buildBuckets = () => {
        const {
            events,
            buckets,
            featureFlags: {
                launchFiveToFollowBucket,
                launchOrgToFollowBucket,
            },
        } = this.props;

        let allFlatBuckets = events;

        if (launchOrgToFollowBucket && !isEmpty(buckets)) {
            const orgToFollowBucket = find(buckets, {key: 'organizers_to_follow'});

            if (orgToFollowBucket) {
                allFlatBuckets = [
                    ...allFlatBuckets,
                    orgToFollowBucket,
                ];
            }
        }

        if (launchFiveToFollowBucket && !isEmpty(buckets)) {
            const fiveToFollowMusicBucket = find(buckets, {key: 'five_to_follow_in_music'});
            const fiveToFollowFoodBucket = find(buckets, {key: 'five_to_follow_in_food_and_drink'});

            if (fiveToFollowMusicBucket) {
                allFlatBuckets = [
                    ...allFlatBuckets,
                    fiveToFollowMusicBucket,
                ];
            }

            if (fiveToFollowFoodBucket) {
                allFlatBuckets = [
                    ...allFlatBuckets,
                    fiveToFollowFoodBucket,
                ];
            }
        }

        return allFlatBuckets;
    }

    render() {
        const {
            featureFlags,
            feedConfig,
            isLoading,
            shouldSuggestReloadingPage,
            tabIndex,
            isAuthenticated,
        } = this.props;
        const allFlatBuckets = this.buildBuckets();
        const allBuckets = getBuckets({buckets: allFlatBuckets, featureFlags, feedConfig});
        const configKey = FEED_TABS[tabIndex];

        let renderedBuckets = (
            <div className="feed-events-bucket">
                <div className="feed-events-bucket__content feed-events-bucket__content--loading">
                    <LoadingBucket />
                </div>
            </div>
        );

        // Only show this when in browser, not server, to avoid hydration mismatch
        if (!isLoading && this.state.isInBrowser) {
            if (allBuckets.length === 0 && shouldSuggestReloadingPage) {
                if (shouldSuggestReloadingPage) {
                    // The server is temporarily unable to return events
                    return (
                        <StateMessage
                            title={gettext('There was an issue loading events')}
                            description={gettext('Try again or check back later')}
                            iconType={<CalendarSVG />}
                            iconSize="medium"
                            primaryLink="/"
                            primaryText={gettext('Try again')}
                        />
                    );
                }
            }

            if (!isAuthenticated && configKey === FOR_YOU) {
                return (
                    <div className="feed-events__empty-state">
                        <StateMessage
                            title={gettext('Discover the best events, picked just for you')}
                            iconType={<HeartSVG />}
                            iconSize="medium"
                            description={gettext('Keep track of your liked events and see events from your favorite organizers when you log in')}
                            primaryText={gettext('Log In')}
                            primaryLink="/login"
                        />
                    </div>
                );
            }

            if (allBuckets.length === 0 && configKey !== FOR_YOU) {
                return (
                    <div className="feed-events__empty-state">
                        <StateMessage
                            title={gettext('No events in your area')}
                            iconType={<CalendarSVG />}
                            iconSize="medium"
                            description={gettext('Try a different location')}
                        />
                    </div>
                );
            } else if (allBuckets.length === 0 && configKey === FOR_YOU) {
                return (
                    <div className="feed-events__empty-state">
                        <StateMessage
                            title={gettext('Discover the best events, picked just for you')}
                            iconType={<UserSVG />}
                            iconSize="medium"
                            description={gettext('Like events and follow organizers to see recommended events here')}
                        />
                    </div>
                );
            }

            renderedBuckets = reduce(allBuckets, (memo, {key, type, bucketConfig, ...bucketProps}, index) => {
                const lastIndex = allBuckets.length - 1;
                const separator = (index < lastIndex) ? <hr key={`separator-${key}`} className="feed-events-bucket__separator" /> : null;

                if (type === 'events') {
                    return [
                        ...memo,
                        <ConnectedEventsBucket
                            key={key}
                            bucketConfig={bucketConfig}
                            bucketKey={key}
                            shouldUseWaypoints={index > 0}
                            shouldShowSeeMore={index === lastIndex}
                            {...bucketProps}
                        />,
                        separator,
                    ];
                }

                if (type === PROFILE_TYPE) {
                    return [
                        ...memo,
                        <ConnectedOrganizersBucket
                            key={key}
                            bucketConfig={bucketConfig}
                            bucketKey={key}
                            shouldUseWaypoints={index > 0}
                            {...bucketProps}
                        />,
                        separator,
                    ];
                }

                if (type === ORGANIZER_ARTICLE_TYPE) {
                    return [
                        ...memo,
                        <ConnectedOrganizerArticleBucket
                            key={key}
                            bucketConfig={bucketConfig}
                            bucketKey={key}
                            shouldUseWaypoints={index > 0}
                            {...bucketProps}
                        />,
                        separator,
                    ];
                }

                return memo;
            }, []);
        }


        return (
            <div className="feed-builder">
                {renderedBuckets}
            </div>
        );
    }
}
